import { ROUTES } from '@/constants/app';
import { ArrowLeft } from 'lucide-react';
import type { ReactNode } from 'react';
import { Link } from 'react-router-dom';
import { LEGAL_DOCUMENT_VERSION } from '../legal.constants';

interface LegalPageLayoutProps {
  title: string;
  children: ReactNode;
}

export function LegalPageLayout({ title, children }: LegalPageLayoutProps) {
  return (
    <div className="min-h-screen bg-neutral-50 text-neutral-900">
      <header className="border-b border-neutral-200 bg-white">
        <div className="mx-auto flex max-w-3xl items-center justify-between gap-4 px-4 py-4 sm:px-6">
          <Link
            to={ROUTES.AUTH.LOGIN}
            className="focus-visible:ring-primary-600 inline-flex min-h-11 items-center gap-2 rounded-lg px-2 text-sm font-medium text-neutral-600 transition-colors hover:bg-neutral-100 hover:text-neutral-900 focus-visible:ring-2 focus-visible:ring-offset-2 focus-visible:outline-none"
          >
            <ArrowLeft className="h-4 w-4" aria-hidden="true" />
            Retour à la connexion
          </Link>
          <span className="text-sm font-semibold text-neutral-900">
            Innov&apos;Paie
          </span>
        </div>
      </header>

      <main className="mx-auto max-w-3xl px-4 py-8 sm:px-6 sm:py-12">
        <header className="mb-10 border-b border-neutral-200 pb-6">
          <h1 className="text-3xl font-semibold tracking-tight text-neutral-950 sm:text-4xl">
            {title}
          </h1>
          <p className="mt-3 text-sm text-neutral-600">
            Version du {LEGAL_DOCUMENT_VERSION}
          </p>
        </header>

        <article className="legal-document space-y-10 text-base leading-7 text-neutral-700">
          {children}
        </article>

        <nav
          aria-label="Documents juridiques"
          className="mt-12 border-t border-neutral-200 pt-6"
        >
          <ul className="flex flex-wrap gap-x-2 gap-y-1 text-sm">
            <li>
              <Link
                to={ROUTES.LEGAL.NOTICES}
                className="focus-visible:ring-primary-600 inline-flex min-h-11 items-center rounded-lg px-2 text-neutral-600 underline decoration-neutral-300 underline-offset-4 hover:bg-neutral-100 hover:text-neutral-950 focus-visible:ring-2 focus-visible:outline-none"
              >
                Mentions légales
              </Link>
            </li>
            <li>
              <Link
                to={ROUTES.LEGAL.PRIVACY}
                className="focus-visible:ring-primary-600 inline-flex min-h-11 items-center rounded-lg px-2 text-neutral-600 underline decoration-neutral-300 underline-offset-4 hover:bg-neutral-100 hover:text-neutral-950 focus-visible:ring-2 focus-visible:outline-none"
              >
                Confidentialité
              </Link>
            </li>
            <li>
              <Link
                to={ROUTES.LEGAL.TERMS}
                className="focus-visible:ring-primary-600 inline-flex min-h-11 items-center rounded-lg px-2 text-neutral-600 underline decoration-neutral-300 underline-offset-4 hover:bg-neutral-100 hover:text-neutral-950 focus-visible:ring-2 focus-visible:outline-none"
              >
                Conditions d&apos;utilisation
              </Link>
            </li>
          </ul>
        </nav>
      </main>
    </div>
  );
}

export function LegalSection({
  title,
  children,
}: {
  title: string;
  children: ReactNode;
}) {
  return (
    <section>
      <h2 className="mb-3 text-xl font-semibold tracking-tight text-neutral-950 sm:text-2xl">
        {title}
      </h2>
      <div className="space-y-4">{children}</div>
    </section>
  );
}
